import type { PerformanceCoreDatapoint, PerformanceDatapoint } from "owa-analytics";
import { type DatapointStatus, type TraceErrorObject } from "owa-analytics-types";
import type { MailboxInfo } from "owa-client-types";
import { isNetworkError } from "owa-errors/lib/isNetworkError";
import { IdbFallbackReason } from "owa-graph-idb-fallback-result";
import { isOffline } from "owa-offline/lib/isOffline";
import { isOfflineMailDataEnabled } from "owa-offline-sync-feature-flags/lib/isOfflineMailDataEnabled";

type SelectMailItemDatapoint = PerformanceCoreDatapoint | PerformanceDatapoint;
type OfflineFailureCategory = "ItemNotSynced" | "DatabaseUnavailable" | "BodyUnavailable" | "Other";

export function addOfflineStartDataToSelectMailItem(datapoint: SelectMailItemDatapoint, mailboxInfo?: MailboxInfo): void {
    datapoint.addCustomData({
        isOfflineAtStart: isOffline(),
        offlineSyncEnabled: isOfflineMailDataEnabled(mailboxInfo),
    });
}

export function addOfflineEndDataToSelectMailItem(datapoint: SelectMailItemDatapoint, status?: DatapointStatus, error?: Error): void {
    const isOfflineAtEnd = isOffline();
    const networkError = !!error && isNetworkError(error.message, error as TraceErrorObject);
    const idbFallbackReason = getIdbFallbackReason(datapoint);
    const isFailure = !!error || status !== undefined;
    const offlineFailureCategory = isFailure && (isOfflineAtEnd || networkError)
        ? getOfflineFailureCategory(idbFallbackReason)
        : undefined;
    
    datapoint.addCustomData({
        isOfflineAtEnd,
        isNetworkError: networkError,
        ...(idbFallbackReason ? { idbFallbackReasonName: idbFallbackReason } : {}),
        ...(offlineFailureCategory ? { offlineFailureCategory } : {}),
    });
}

function getIdbFallbackReason(datapoint: SelectMailItemDatapoint): string | undefined {
    const idbFallbackReason = datapoint.getCustomData("idbFallbackReason");
    if (typeof idbFallbackReason === "number") {
        return IdbFallbackReason[idbFallbackReason];
    }
    return typeof idbFallbackReason === "string" ? idbFallbackReason : undefined;
}

function getOfflineFailureCategory(idbFallbackReason: string | undefined): OfflineFailureCategory {
    switch (idbFallbackReason) {
        case "NotFound":
            return "ItemNotSynced";
        case "NoDatabase":
            return "DatabaseUnavailable";
        case "BodyShapeMismatch":
            return "BodyUnavailable";
        default:
            return "Other";
    }
}
